import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type { components } from '@bookingdei/api-client/types';
import { api } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { useResource } from '../../shell/use-resource';
import { useSettings } from '../../shell/settings-context';
import { ErrorState } from '../../shell/state-view';
import { HeroSearch } from './hero-search';
import { HeroTickerRow } from './hero-ticker-row';
import type { PlaceOption } from './hero-location-panel';
import { OffersRailSection } from './offers-rail-section';
import { ReelsRailSection } from './reels-rail-section';
import { CommunityMarquee } from './community-marquee';
import { PopularSection } from './popular-section';
import { TrendingRailSection } from './trending-rail-section';
import { BusinessCarousel } from './business-carousel';
import { placeSponsored } from './place-sponsored';
import { showcaseProfile } from './showcase-profile';
import { demoShowcase } from '../../theme/demo-showcase';
import { useHomeFormat } from './use-home-format';
import { useHomeMotion } from './use-home-motion';
import './home.css';

// The bands below the carousels are their own chunk (home-below-fold.tsx). The fetch
// starts as soon as this module loads, in parallel with the API calls, so they are
// usually there before the visitor scrolls to them.
const belowFold = import('./home-below-fold');
const HomeBelowFold = lazy(() => belowFold.then((m) => ({ default: m.HomeBelowFold })));

type Home = components['schemas']['Home'];
type LocationList = components['schemas']['LocationList'];
type LocationNode = components['schemas']['LocationNode'];
type SearchResults = components['schemas']['SearchResults'];
type Trending = components['schemas']['Trending'];
type BusinessPage = components['schemas']['BusinessPage'];
type DirectoryOfferList = components['schemas']['DirectoryOfferList'];

// "Things to do this weekend" draws from these verticals (design weekend rail).
const WEEKEND_CATEGORIES = new Set(['event', 'class', 'turf']);

function toPlaces(tree: LocationNode[], lang: string): { cities: PlaceOption[]; all: PlaceOption[] } {
  const cities: PlaceOption[] = [];
  const all: PlaceOption[] = [];
  const name = (n: LocationNode) => (lang === 'bn' ? n.name_bn : n.name_en) ?? n.name_en;
  const walk = (nodes: LocationNode[]) => {
    for (const n of nodes) {
      if (n.level === 'city') {
        const city: PlaceOption = { id: n.id, slug: n.slug, label: name(n), cityLabel: '', isCity: true };
        cities.push(city);
        all.push(city);
        for (const a of n.children ?? []) {
          if (a.level === 'area') all.push({ id: a.id, slug: a.slug, label: name(a), cityLabel: city.label, isCity: false });
        }
      } else walk(n.children ?? []);
    }
  };
  walk(tree);
  return { cities, all };
}

// Home (PRD §7.3 route 1, FR-C1, FR-C19–C22), converted from the design export
// (DC:261-1029) section by section:
//
//   hero + pill search + live counter → offers near you → community marquee →
//   popular near you (+ browse) → trending → recommended / new / weekend carousels →
//   app download → trust stats → for business → customer reviews → browse by area
//
// Every band renders from live data and is absent when it has none; carousels show
// skeleton cards while their rail loads.
export function HomeScreen() {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const rootRef = useRef<HTMLDivElement>(null);

  const home = useResource<Home>((signal) => api.GET('/home', { signal, lang }), [lang]);
  const locations = useResource<LocationList>((signal) => api.GET('/registries/locations', { signal, lang }), [lang]);
  const trending = useResource<Trending>((signal) => api.GET('/trending', { signal, lang }), [lang]);
  // Deals businesses announced themselves (FR-A26 v3) share the offers rail with the bookable ones.
  const deals = useResource<DirectoryOfferList>((signal) => api.GET('/offers/directory', { signal, lang, params: { query: { limit: 12 } } }), [lang]);
  const newest = useResource<SearchResults>(
    (signal) => api.GET('/search', { params: { query: { sort: 'newest', limit: 10 } }, signal, lang }),
    [lang],
  );
  const weekend = useResource<SearchResults>(
    (signal) => api.GET('/search', { params: { query: { sort: 'trending', limit: 50 } }, signal, lang }),
    [lang],
  );

  const categories = useMemo(() => home.data?.categories ?? [], [home.data]);
  const fmt = useHomeFormat(categories);
  const featured = home.data?.featured ?? [];
  const nearby = home.data?.nearby ?? [];
  const showcaseSlug = featured.find((b) => !b.sponsored)?.slug;
  const showcase = useResource<BusinessPage | undefined>(
    (signal) =>
      showcaseSlug
        ? api.GET('/businesses/{slug}/page', { params: { path: { slug: showcaseSlug } }, signal, lang })
        : Promise.resolve(undefined),
    [showcaseSlug, lang],
  );
  const now = useMemo(() => new Date(), []);
  const real = showcase.data ? showcaseProfile(showcase.data, lang, now) : null;
  // The mockups need a business with a service menu and a team. With none featured, or
  // the featured one still bare (an imported listing), they show the theme's demo
  // business instead of disappearing — once home has answered, so a real one never
  // flashes the demo first.
  const settled = Boolean(home.data) && (!showcaseSlug || !showcase.loading);
  const demo = useMemo(() => demoShowcase(lang), [lang]);
  const useDemo = settled && (!real || real.staff.length === 0 || real.serviceCount === 0);
  const profile = useDemo ? demo.profile : real;
  const { cities, all: places } = useMemo(() => toPlaces(locations.data?.items ?? [], lang), [locations.data, lang]);

  // Re-run the scroll reveal once the lazy bands mount so they animate in too.
  const [belowReady, setBelowReady] = useState(false);
  useEffect(() => {
    let live = true;
    void belowFold.then(() => live && setBelowReady(true));
    return () => {
      live = false;
    };
  }, []);
  useHomeMotion(rootRef, [home.data, trending.data, belowReady]);

  if (home.error) return <ErrorState onRetry={home.reload} />;

  const appUrl = settings?.web_base_url ? `${settings.web_base_url.replace(/\/$/, '')}/app` : undefined;
  const recommended = home.data ? placeSponsored(featured, 3) : undefined;
  const recoHasPlacement = Boolean(recommended?.some((b) => b.sponsored));
  const weekendItems = weekend.data
    ? placeSponsored((weekend.data.items ?? []).filter((b) => WEEKEND_CATEGORIES.has(b.category_id)), 2, 4)
    : undefined;
  const photos = [...featured, ...nearby]
    .map((b) => b.cover_image?.['400'] ?? b.cover_image?.['800'])
    .filter((u): u is string => Boolean(u));
  const bizNames = new Map<string, string>();
  for (const b of [...featured, ...nearby, ...(trending.data?.top10 ?? []).map((e) => e.business), ...(newest.data?.items ?? [])]) {
    bizNames.set(b.id, lang === 'bn' && b.name_bn ? b.name_bn : b.name);
  }
  const stats = home.data?.stats;

  return (
    <div className="hm" ref={rootRef}>
      <section className="hm-hero">
        <div className="hm-hero__inner">
          <h1 className="hm-hero__title">{t('dc50.hero.title')}</h1>
          <p className="hm-hero__sub">{t('dc50.hero.sub')}</p>
          <HeroSearch categories={categories} places={places} />
          <HeroTickerRow bookingsToday={stats?.bookings_today} appUrl={appUrl} />
        </div>
      </section>

      {home.data?.offers?.length || deals.data?.items?.length ? (
        <OffersRailSection offers={home.data?.offers ?? []} deals={deals.data?.items ?? []} fmt={fmt} />
      ) : null}
      <ReelsRailSection />
      <CommunityMarquee photos={photos} />
      <PopularSection nearby={nearby} categories={categories} cities={cities} fmt={fmt} />
      <TrendingRailSection items={(trending.data?.top10 ?? []).map((e) => e.business)} fmt={fmt} />

      <BusinessCarousel
        title={t('dc50.car.recommended')}
        items={recommended}
        footnote={recoHasPlacement ? t('dc50.car.reco_note') : undefined}
        fmt={fmt}
      />
      <BusinessCarousel title={t('dc50.car.new')} items={newest.data?.items} fmt={fmt} />
      <BusinessCarousel
        title={t('dc50.car.weekend')}
        sub={t('dc50.car.weekend_sub')}
        seeAllTo="/search?sort=trending"
        footnote={t('dc50.popular.rankings_note')}
        items={weekendItems}
        fmt={fmt}
      />

      <Suspense fallback={null}>
        <HomeBelowFold
          app={{ appUrl, profile, results: nearby.filter((b) => !b.sponsored).slice(0, 4), fmt }}
          showcase={{
            profile,
            customers: useDemo ? demo.customers : (showcase.data?.reviews.items ?? []).map((r) => r.author_name ?? '').filter(Boolean),
            services: useDemo ? demo.services : (showcase.data?.catalog ?? []).map((c) => (lang === 'bn' ? c.name_bn : c.name_en) ?? c.name_en ?? '').filter(Boolean),
            merchantReviews: stats?.merchant_reviews,
            mediaCount: useDemo ? demo.mediaCount : (showcase.data?.business.media?.length ?? 0),
            fmt,
          }}
          reviews={{ reviews: home.data?.review_highlights ?? [], businessName: (id) => bizNames.get(id) }}
          areas={{ locations: locations.data?.items ?? [], categories }}
        />
      </Suspense>
    </div>
  );
}
